import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import {
  InvoiceStatus,
  UpdateInvoiceInput,
  UpsertPayableLineItemInput,
} from "@/gql/graphql";
import { InvoiceDetailsForm } from "@/app/Invoices/Forms/InvoiceDetailsForm";
import { PayableLineItemTable } from "@/app/Invoices/Components/ViewInvoiceLineItems";
import { AddInvoicePaymentButton } from "@/app/Invoices/Components/AddInvoicePaymentButton";
import { InvoiceLineItemsForm } from "@/app/Invoices/Forms/InvoiceLineItemsForm";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { InvoicePayeeCard } from "@/app/Invoices/Components/InvoicePayeeCard";
import { DescriptionList } from "@/lib/Components/DescriptionList/DescriptionListCard";
import { formatDate } from "@/lib/Formatters/formatDate";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  updateInvoiceMutation,
  createInvoiceCheckoutSessionMutation,
  createInvoicePdfMutation,
} from "@/app/Invoices/GraphQL/invoiceMutations";
import { useInvoiceRecord } from "@/app/Invoices/Hooks/useInvoiceRecord";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { CreditCardIcon } from "@heroicons/react/20/solid";
import { cn } from "@/lib/utils";
import { Button } from "@/components/catalyst/button";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { PreviewPdfDialog } from "@/lib/Components/Dialog/PreviewPdfDialog";
import { useTranslations } from "use-intl";

export function InvoiceDetailScreen() {
  const record = useInvoiceRecord();
  const { mutateAsync } = useGqlMutation(updateInvoiceMutation);
  const { mutateAsync: createCheckout } = useGqlMutation(
    createInvoiceCheckoutSessionMutation,
  );
  const { mutateAsync: createPdf } = useGqlMutation(createInvoicePdfMutation);
  const { isAdmin } = useTenant();
  const { open: openPdf } = useDialog(PreviewPdfDialog);
  const t = useTranslations("invoice.detail");

  const isUnpaid =
    record.amount_due > 0 && record.status !== InvoiceStatus.Draft;
  const canPayOnline = [InvoiceStatus.Issued, InvoiceStatus.Overdue].includes(
    record.status,
  );

  async function handlePayNow() {
    const res = await createCheckout({
      id: record.id,
    });

    window.open(
      res.createInvoiceCheckoutSession.checkout_session_url,
      "_blank",
    );
  }

  async function handleDownloadPdf() {
    const res = await createPdf({
      id: record.id,
    });
    openPdf({
      url: res.createInvoicePdf,
    });
  }

  return (
    <div className="space-y-12 py-6">
      {record.status !== InvoiceStatus.Draft ? (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {t("invoiceSummary")}
            </h3>

            {isAdmin ? <AddInvoicePaymentButton /> : null}
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div
                className={cn(
                  "rounded-lg border p-4",
                  isUnpaid
                    ? "border-yellow-200 bg-yellow-50"
                    : "border-green-200 bg-green-50",
                )}
              >
                <dt className="mb-1 text-sm font-medium text-gray-600">
                  {t("amountDue")}
                </dt>
                <dd className="text-3xl font-bold text-gray-900">
                  {formatCurrency(record.amount_due, record.currency, {
                    showZero: true,
                  })}
                </dd>
                {record.due_date ? (
                  <p className="mt-1 text-sm text-gray-500">
                    {t("due")} {formatDate(record.due_date)}
                    {record.status === InvoiceStatus.Overdue
                      ? ` ${t("overdue")}`
                      : null}
                  </p>
                ) : null}
              </div>

              <div className="rounded-lg bg-gray-50 p-4">
                <dt className="mb-1 text-sm font-medium text-gray-600">
                  {t("totalAmount")}
                </dt>
                <dd className="text-3xl font-bold text-gray-900">
                  {formatCurrency(record.amount_inc_tax, record.currency, {
                    showZero: true,
                  })}
                </dd>
              </div>

              <div className="rounded-lg bg-gray-50 p-4">
                <dt className="mb-1 text-sm font-medium text-gray-600">
                  {t("amountPaid")}
                </dt>
                <dd className="text-3xl font-bold text-gray-900">
                  {formatCurrency(record.amount_paid, record.currency, {
                    showZero: true,
                  })}
                </dd>
                {record.amount_refunded > 0 ? (
                  <p className="mt-1 text-sm text-gray-500">
                    {formatCurrency(record.amount_refunded, record.currency)}{" "}
                    {t("refunded")}
                  </p>
                ) : null}
              </div>
            </div>

            {canPayOnline ? (
              <div className="mt-6 flex flex-wrap gap-3">
                <Button onClick={handlePayNow}>
                  <CreditCardIcon />
                  {t("payOnline")}
                </Button>

                <Button outline onClick={handleDownloadPdf}>
                  <ArrowDownTrayIcon />
                  {t("downloadPdf")}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {t("invoiceDetails")}
          </h3>
        </div>
        <div className="space-y-8 p-6">
          <RecordScreenFormCard<UpdateInvoiceInput>
            isEditable={isAdmin}
            initialValues={{
              payee: {
                connect: record.payee.id,
              },
              invoice_date: record.invoice_date,
              due_date: record.due_date,
              purchase_order_number: record.purchase_order_number,
            }}
            onSubmit={async (values) => {
              await mutateAsync({
                id: record.id,
                input: values,
              });
            }}
            title={t("details")}
            view={
              <div className="grid grid-cols-2 gap-6">
                <InvoicePayeeCard invoice={record} />
                <DescriptionList
                  items={[
                    {
                      key: t("poNumber"),
                      value: record.purchase_order_number ?? "--",
                    },
                    {
                      key: t("invoiceDate"),
                      value: formatDate(record.invoice_date),
                    },
                    {
                      key: t("dueDate"),
                      value: formatDate(record.due_date),
                    },
                  ]}
                />
              </div>
            }
          >
            <InvoiceDetailsForm invoice={record} />
          </RecordScreenFormCard>

          <RecordScreenFormCard<UpdateInvoiceInput>
            enableReinitialize={true}
            isEditable={isAdmin}
            initialValues={{
              lineItems: {
                upsert: record.lineItems.map((lineItem) => {
                  return {
                    id: lineItem.id,
                    unit_amount: lineItem.unit_amount,
                    quantity: lineItem.quantity,
                    description: lineItem.description,
                    tax_type: lineItem.tax_type,
                    fulfills: lineItem.fulfills
                      ? {
                          update: {
                            id: lineItem.fulfills?.id ?? null!,
                          },
                        }
                      : undefined,
                  } satisfies UpsertPayableLineItemInput;
                }),
                delete: [],
              },
            }}
            onSubmit={async (values) => {
              await mutateAsync({
                id: record.id,
                input: values,
              });
            }}
            title={t("lineItems")}
            view={<PayableLineItemTable invoice={record} />}
          >
            <InvoiceLineItemsForm
              payeeId={record.payee.id}
              currency={record.currency}
              invoice={record}
            />
          </RecordScreenFormCard>
        </div>
      </div>
    </div>
  );
}
